import { useRef, useState } from "react";

import { MessageCircle } from "lucide-react";

import { track } from "../analytics/publicAnalytics";
import { Link } from "../components/Link";
import { Logo } from "../components/Logo";
import { ManualMenu } from "../components/ManualMenu";
import { SITE_CONTACT, SITE_NAME_WITH_FORMER } from "../seo/site";

import {
  useHeaderChapter,
  useRiseOnEnter,
  useScrollScale,
} from "./landing/useLandingMotion";

import { FAQS, FEATURES, FREE_NOTE, STEPS } from "./landing/content";

const NAV = [
  { href: "#features", label: "기능" },
  { href: "#start", label: "시작하기" },
  { href: "#faq", label: "질문" },
] as const;

export const LandingPage = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const showcaseRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [covered, setCovered] = useState(false);
  const chapter = useHeaderChapter(rootRef);
  useRiseOnEnter(rootRef);
  useScrollScale(showcaseRef);

  const toggleCover = () => {
    const next = !covered;
    setCovered(next);
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (next) {
      video.pause();
    } else {
      void video.play()?.catch(() => undefined);
    }
  };

  return (
    <div className="canvaschool-landing" ref={rootRef}>
      <header
        className="canvaschool-landing-header canvaschool-landing-bar"
        data-chapter-ink={chapter}
      >
        <Link to="/" className="canvaschool-brand">
          <Logo size={28} inverted={chapter === "navy"} />
          크루바스
        </Link>
        <nav className="cl-nav" aria-label="바로가기">
          {NAV.map((item) => (
            <a key={item.href} href={item.href} className="cl-nav-anchor">
              {item.label}
            </a>
          ))}
          <ManualMenu variant="landing" />
          <Link to="/login" className="cl-nav-login">
            로그인
          </Link>
          <Link
            to="/login"
            className="cl-btn cl-btn--accent cl-nav-cta"
            onClick={() => track("beta_cta_click", { cta_position: "header" })}
          >
            무료로 시작하기
          </Link>
        </nav>
      </header>

      <section className="cl-hero" data-chapter="navy" aria-label="크루바스">
        <div className="cl-hero-paper" data-chapter="paper" />
        <div className="cl-title" data-rise>
          <h1 className="cl-title-ink">
            수업의 시선이
            <br />
            정렬되는 곳
            <span className="cl-title-on-ink" aria-hidden="true">
              수업의 시선이
              <br />
              정렬되는 곳
            </span>
          </h1>
        </div>
        <div className="cl-hero-foot">
          <p className="cl-hero-lead" data-rise>
            교사가 설계한 프레임 안에 학생 화면을 묶어 두는 학급 캔버스입니다.
            <span className="cl-hero-name">{SITE_NAME_WITH_FORMER}</span>
          </p>
          <div className="cl-hero-action" data-rise>
            <Link
              to="/login"
              className="cl-btn cl-btn--accent cl-btn--tall"
              onClick={() => track("beta_cta_click", { cta_position: "hero" })}
            >
              무료로 시작하기
            </Link>
          </div>
        </div>
      </section>

      <section
        className="cl-showcase"
        data-chapter="navy"
        aria-label="화면 가리기 체험"
      >
        <div className="cl-showcase-inner">
          <div className="cl-video-wrap" data-rise="media">
            <div className="cl-video-frame" ref={showcaseRef}>
              <video
                ref={videoRef}
                src="/landing/hero.mp4"
                poster="/landing/hero-poster.jpg"
                aria-label="학생 16명이 각자 자기 프레임에 그림을 그려 완성하는 교사 화면"
                className="cl-video"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
              />
              <div
                className="cl-cover"
                data-covered={covered}
                aria-hidden={!covered}
              >
                <span>선생님을 보세요</span>
              </div>
            </div>
          </div>
          <div className="cl-showcase-bar" data-rise>
            <p>교사 화면에서 누르는 바로 그 버튼입니다. 눌러 보세요.</p>
            <button
              type="button"
              className="cl-btn cl-btn--paper"
              aria-pressed={covered}
              onClick={toggleCover}
            >
              {covered ? "화면 보여주기" : "화면 가리기"}
            </button>
          </div>
        </div>
      </section>

      <section
        className="cl-paper-news"
        data-chapter="surface"
        aria-label="모둠 신문"
      >
        <div className="cl-grid">
          <h2 className="cl-name cl-news-title" data-rise>
            역할은 나누고
            <br />
            지면은 함께 쓰는 곳
          </h2>
          <p className="cl-news-text" data-rise>
            모둠원은 칸이 아니라 역할을 나눕니다. 제목·기사·사진·그림을 맡은 네
            명이 한 장 위에서 동시에 움직이고, 교사 화면에는 네 모둠의 신문이
            한꺼번에 완성되어 갑니다.
          </p>
          <video
            src="/landing/hero-newspaper.mp4"
            poster="/landing/hero-newspaper-poster.jpg"
            aria-label="네 모둠 학생 16명이 역할을 나눠 환경 신문 네 장을 함께 만드는 교사 화면"
            className="cl-video cl-news-video"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            data-rise="media"
          />
        </div>
      </section>

      <section
        className="cl-features"
        id="features"
        data-chapter="paper"
        aria-labelledby="canvaschool-features-title"
      >
        <div className="cl-grid cl-features-head">
          <h2
            id="canvaschool-features-title"
            className="cl-name cl-muted"
            data-rise
          >
            교실이라서 필요한 것들
          </h2>
          <p className="cl-side-lead" data-rise>
            화이트보드 도구로는 안 되던 것들을, 교실에 맞게 다시 설계했습니다.
          </p>
        </div>
        <div className="cl-feature-list">
          {FEATURES.map((feature, index) => (
            <article
              key={feature.slug}
              className={
                index % 2 === 1
                  ? "cl-grid cl-feature cl-feature--flip"
                  : "cl-grid cl-feature"
              }
            >
              <div className="cl-feature-copy">
                <h3 className="cl-name" data-rise>
                  {feature.name}
                </h3>
                <p data-rise>
                  {feature.lead} {feature.body}
                </p>
              </div>
              <img
                className="cl-shot"
                src={`/landing/feature-${feature.slug}.webp`}
                alt={feature.alt}
                width={1600}
                height={1000}
                loading="lazy"
                decoding="async"
                data-rise="media"
              />
            </article>
          ))}
        </div>
      </section>

      <section
        className="cl-start"
        id="start"
        data-chapter="paper"
        aria-labelledby="cl-start-title"
      >
        <div className="cl-grid cl-start-grid">
          <h2
            id="cl-start-title"
            className="cl-step cl-muted cl-step--0"
            data-rise
          >
            이렇게 시작해요
          </h2>
          <ol className="cl-steps">
            {STEPS.map((step, index) => (
              <li
                key={step.title}
                className={`cl-step-item cl-step-item--${index + 1}`}
                data-rise
              >
                <h3 className="cl-step">{step.title}</h3>
                <p>{step.body}</p>
              </li>
            ))}
          </ol>
          <p className="cl-start-more" data-rise>
            <Link to="/manual">실제 화면으로 하나씩 보기 →</Link>
          </p>
        </div>
      </section>

      <section
        className="cl-faq"
        id="faq"
        data-chapter="surface"
        aria-labelledby="cl-faq-title"
      >
        <div className="cl-grid cl-faq-grid">
          <h2 id="cl-faq-title" className="cl-q cl-muted" data-rise>
            자주 묻는 질문
          </h2>
          {FAQS.map((item) => (
            <div key={item.q} className="cl-faq-row" data-rise>
              <h3 className="cl-q">{item.q}</h3>
              <p>{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section
        className="cl-beta"
        id="beta"
        data-chapter="navy"
        aria-labelledby="canvaschool-beta-title"
      >
        <div className="cl-grid cl-beta-grid">
          <h2 id="canvaschool-beta-title" className="cl-beta-title" data-rise>
            우리 반 화면,
            <br />
            이제 앞을 봅니다
          </h2>
          <div className="cl-beta-text" data-rise>
            <p className="cl-lead">{FREE_NOTE}</p>
            <p className="cl-lead">
              교사라면 누구나 구글 계정으로 바로 시작할 수 있습니다. 신청하고
              기다릴 필요가 없습니다. 학생은 이메일이 필요 없습니다.
            </p>
            <p className="cl-small">
              더 나은 서비스를 위해 오류와 의견이 수집됩니다.{" "}
              <a href="/privacy.html">수집 안내 보기</a>
            </p>
            <p className="cl-small">
              바라는 기능이나 요청은 언제든{" "}
              <a href={`mailto:${SITE_CONTACT}`}>{SITE_CONTACT}</a>로 보내
              주세요. 모두 읽고 반영합니다.
            </p>
          </div>
          <div className="cl-beta-action" data-rise>
            <Link
              to="/login"
              className="cl-btn cl-btn--accent cl-btn--tall"
              onClick={() =>
                track("beta_cta_click", { cta_position: "beta_section" })
              }
            >
              무료로 시작하기
            </Link>
          </div>
        </div>
      </section>

      <footer className="cl-footer" data-chapter="navy">
        <div className="cl-footer-fit">
          <p className="cl-footer-name">크루바스</p>
        </div>
        <div className="cl-grid cl-footer-line">
          <p>수업의 시선이 정렬되는 곳, {SITE_NAME_WITH_FORMER}</p>
          <nav aria-label="푸터 링크">
            <Link to="/login">로그인</Link>
            <Link to="/manual">이용 방법</Link>
            <Link
              to="/login"
              onClick={() =>
                track("beta_cta_click", { cta_position: "footer" })
              }
            >
              무료로 시작하기
            </Link>
            <a href="/privacy.html">오류·의견 수집 안내</a>
            <a href={`mailto:${SITE_CONTACT}`} className="cl-footer-contact">
              <MessageCircle size={18} aria-hidden="true" />
              의견·요청 {SITE_CONTACT}
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
};
